<template>
  <div class="w-full" @click.stop="emit('click', $event)">
    <b-label v-if="props.label || props.description" :for="props.id">
      <span v-if="props.label" class="block text-sm text-white font-normal">{{ props.label }}</span>
      <span v-if="props.description" class="block text-gray-400 text-xs mt-1 mb-3">{{ props.description }}</span>
    </b-label>
    <div
      :class="[
        'h-[50px] relative focus-within:outline-2 focus-within:border-primary flex items-center w-full bg-transparent text-sm border border-[#373737] rounded-lg placeholder-gray-500 text-white',
        {
          'border border-red-500': errorMessage
        }
      ]"
    >

      <slot name="prependIcon">
        <div v-if="props.prependIcon" class="pl-3 absolute left-0 top-1/2 transform -translate-y-1/2">
          <icon
            :name="props.prependIcon"
            class="text-gray-400 mt-1"
            size="18"
          />
        </div>
      </slot>

      <input
        :type="props.type"
        :class="['h-[50px] w-full bg-transparent rounded-lg focus:outline-none', {
          'px-10': (props.prependIcon || $slots.appendIcon) && (props.appendIcon || $slots.appendIcon),
          'pl-10': (props.prependIcon || $slots.appendIcon) && (!props.appendIcon || !$slots.appendIcon),
          'pr-10': (!props.prependIcon || !$slots.appendIcon) && (props.appendIcon || $slots.appendIcon),
          'px-3': (!props.prependIcon || !$slots.appendIcon) && (!props.appendIcon || !$slots.appendIcon),
        }]"
        :placeholder="props.placeholder"
        :maxlength="props.maxlength"
        :value="inputValue || props.modelValue"
        :readonly="props.readonly"
        :pattern="props.pattern"
        :autocomplete="props.autocomplete"
        :inputmode="props.inputmode"
        @input="onInput"
        @blur="onBlur"
        :id="props.id"
        :min="props.min"
        :step="props.step"
        v-bind="$attrs"
      />

      <slot name="append">
        <div v-if="props.appendIcon" class="absolute right-0 top-1/2 transform -translate-y-1/2 pr-3">
          <icon
            :name="props.appendIcon"
            class="text-gray-400"
          />
        </div>
      </slot>
    </div>

    <transition>
      <div v-if="errorMessage" class="text-red-500  rounded-b-xl  pt-3 pb-1 -mt-3">
        <small>{{ errorMessage }}</small>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import {useField} from "vee-validate";
import BLabel from "./BLabel.vue";

defineOptions({
  inheritAttrs: false
});

interface Props {
  label?: string;
  description?: string;
  placeholder?: string;
  modelValue: string|number;
  maxlength?: number;
  readonly?: Boolean;
  type?: 'text' | 'number' | 'email' | 'password';
  disabled?: boolean;
  pattern?: string;
  inputmode?: 'numeric';
  rules?: string | Record<string, any> | Function;
  name?: string;
  autocomplete?: AutoFill
  appendIcon?: string;
  prependIcon?: string;
  id?: string;
  min?: string;
  step?: string | number;
}

const props = withDefaults(defineProps<Props>(), {
  name: 'name_' + new Date().getTime(),
  type: 'text',
  maxlength: 255,
  autocomplete: 'off',
})

const nameRef = toRef(props, 'name');
const {
  value: inputValue,
  errorMessage,
  handleChange,
  validate,
} = useField(nameRef, props.rules, {
  initialValue: props.modelValue,
  validateOnValueUpdate: false,
});

const onBlur = (event: FocusEvent) => {
  const value = (event.target as HTMLInputElement).value;
  if (value !== '') {
    validate();
  }
};

const emit = defineEmits<{
  'update:modelValue': [value: string | number],
  'click': Event
}>();

const onInput = (event: InputEvent) => {
  handleChange(event);
  emit("update:modelValue", (event.target as HTMLInputElement).value);
};
</script>

<style>
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active{
  -webkit-background-clip: text;
  -webkit-text-fill-color: #ffffff;
  transition: background-color 5000s ease-in-out 0s;
}
</style>
